import { useCallback, useEffect, useRef } from 'react'
import { useFilterOptions } from '../hooks/useQueries'
import useStore from '../store/useStore'
import { track } from '../umami'

interface Props {
  siteCount: number
}

export default function Header({ siteCount }: Props) {
  const { data: filterOpts } = useFilterOptions()
  const countryFilter = useStore((s) => s.countryFilter)
  const setCountryFilter = useStore((s) => s.setCountryFilter)
  const setSearchQuery = useStore((s) => s.setSearchQuery)

  const searchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined)
  const trackTimeout = useRef<ReturnType<typeof setTimeout>>(undefined)

  const handleCountryChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const country = e.target.value
      setCountryFilter(country)
      if (country) track('country_filter', { country })
    },
    [setCountryFilter],
  )

  const handleSearchInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      clearTimeout(searchTimeout.current)
      clearTimeout(trackTimeout.current)

      searchTimeout.current = setTimeout(() => {
        setSearchQuery(raw.toLowerCase())
      }, 250)

      trackTimeout.current = setTimeout(() => {
        const q = raw.trim()
        if (q) track('site_searched', { query: q })
      }, 500)
    },
    [setSearchQuery],
  )

  useEffect(() => {
    return () => {
      clearTimeout(searchTimeout.current)
      clearTimeout(trackTimeout.current)
    }
  }, [])

  return (
    <header id="header">
      <h1>ArcheoMap</h1>
      <div id="filters">
        <label htmlFor="filter-country" className="visually-hidden">
          Filter sites by country
        </label>
        <select id="filter-country" value={countryFilter} onChange={handleCountryChange}>
          <option value="">All Countries</option>
          {filterOpts?.countries.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <label htmlFor="filter-search" className="visually-hidden">
          Search archaeological sites by name
        </label>
        <input
          type="text"
          id="filter-search"
          placeholder="Search sites..."
          onChange={handleSearchInput}
        />
        <span id="site-count">{siteCount} sites</span>
      </div>
    </header>
  )
}
